/**
 * 3:4 overlay product tile behavior — port of ProductTile.tsx:
 * - hover shows arrow controls (only when multiple images exist)
 * - arrows cycle images (index 0 = base; alt images fade 350ms)
 * - mouse leave resets to the base image
 * - quick add: bag button adds 1× the tile variant via /cart/add.js, a
 *   miniature of the tile image flies into the header bag icon, badges update
 *   (drawer stays closed — the flight + badge bump is the feedback)
 */
(function () {
  'use strict';

  var FLY_MS = 650;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function visibleCartIcon() {
    var icons = document.querySelectorAll('[data-cart-icon]');
    for (var i = 0; i < icons.length; i++) {
      var rect = icons[i].getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return icons[i];
    }
    return null;
  }

  function bumpBadge() {
    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      var rect = badge.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return;
      badge.classList.remove('is-bumped');
      void badge.offsetWidth; // restart the animation on rapid re-adds
      badge.classList.add('is-bumped');
    });
  }

  /**
   * Miniature of the visible tile image flies from the tile into the header
   * bag icon. Arc comes from split easing: X moves linearly (outer element),
   * Y accelerates (inner element). Resolves when the flight lands.
   */
  function flyToCart(tile) {
    return new Promise(function (resolve) {
      var icon = visibleCartIcon();
      var frame = tile.querySelector('.product-tile__frame');
      var img =
        tile.querySelector('.product-tile__alt.is-current') ||
        tile.querySelector('.product-tile__base');
      if (!icon || !frame || !img || !img.currentSrc && !img.src) return resolve();
      if (prefersReducedMotion() || typeof frame.animate !== 'function') return resolve();

      var from = frame.getBoundingClientRect();
      var to = icon.getBoundingClientRect();
      var size = 64;
      var startX = from.left + from.width / 2 - size / 2;
      var startY = from.top + Math.min(from.height / 2, 120) - size / 2;
      var dx = to.left + to.width / 2 - (startX + size / 2);
      var dy = to.top + to.height / 2 - (startY + size / 2);

      var fly = document.createElement('div');
      fly.className = 'cart-fly';
      fly.style.left = startX + 'px';
      fly.style.top = startY + 'px';
      var inner = document.createElement('div');
      inner.className = 'cart-fly__inner';
      var mini = document.createElement('img');
      mini.src = img.currentSrc || img.src;
      mini.alt = '';
      inner.appendChild(mini);
      fly.appendChild(inner);
      document.body.appendChild(fly);

      fly.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(' + dx + 'px)' }],
        { duration: FLY_MS, easing: 'cubic-bezier(0.3, 0.1, 0.6, 0.9)', fill: 'forwards' }
      );
      var drop = inner.animate(
        [
          { transform: 'translateY(0) scale(1)', opacity: 1 },
          { transform: 'translateY(' + dy + 'px) scale(0.15)', opacity: 0.35 }
        ],
        { duration: FLY_MS, easing: 'cubic-bezier(0.55, 0, 0.85, 0.4)', fill: 'forwards' }
      );
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        fly.remove();
        resolve();
      };
      drop.onfinish = finish;
      setTimeout(finish, FLY_MS + 200); // safety net if onfinish never fires
    });
  }

  function announce(message) {
    var region = document.querySelector('[data-tile-live-region]');
    if (!region) {
      region = document.createElement('div');
      region.setAttribute('data-tile-live-region', '');
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = message;
  }

  function init(tile) {
    if (tile.dataset.tileInit) return;
    tile.dataset.tileInit = '1';

    var altImages = Array.prototype.slice.call(tile.querySelectorAll('.product-tile__alt'));
    var imageCount = altImages.length + 1; // + base image
    var imgIdx = 0;

    function renderImages() {
      altImages.forEach(function (img, i) {
        img.classList.toggle('is-current', i + 1 === imgIdx);
      });
    }

    tile.addEventListener('mouseenter', function () {
      tile.classList.add('is-hovered');
    });

    tile.addEventListener('mouseleave', function () {
      tile.classList.remove('is-hovered');
      imgIdx = 0;
      renderImages();
    });

    var prevBtn = tile.querySelector('[data-tile-prev]');
    var nextBtn = tile.querySelector('[data-tile-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        imgIdx = (imgIdx - 1 + imageCount) % imageCount;
        renderImages();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        imgIdx = (imgIdx + 1) % imageCount;
        renderImages();
      });
    }

    var addBtn = tile.querySelector('[data-tile-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (addBtn.classList.contains('is-busy')) return;
        var variantId = parseInt(addBtn.getAttribute('data-variant-id'), 10);
        if (!variantId) return;

        addBtn.classList.add('is-busy');
        addBtn.classList.remove('is-added');

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function (res) {
            return res.json().then(function (data) { return { ok: res.ok, data: data }; });
          })
          .then(function (result) {
            if (!result.ok) {
              throw new Error(result.data && (result.data.description || result.data.message));
            }
            return Promise.all([
              fetch('/cart.js').then(function (res) { return res.json(); }),
              flyToCart(tile)
            ]);
          })
          .then(function (results) {
            addBtn.classList.remove('is-busy');
            addBtn.classList.add('is-added');
            setTimeout(function () { addBtn.classList.remove('is-added'); }, 1600);
            announce(addBtn.getAttribute('data-added-label') || '');
            document.dispatchEvent(new CustomEvent('dylys:cart-change', {
              detail: { cart: results[0], source: 'tile' }
            }));
            bumpBadge();
          })
          .catch(function () {
            // Rare (e.g. just sold out) — fall back to the PDP where the full
            // form explains the state instead of inventing a tile error UI.
            addBtn.classList.remove('is-busy');
            window.location.href = tile.getAttribute('href') || '/cart';
          });
      });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-product-tile]').forEach(init);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  window.Dylys = window.Dylys || {};
  window.Dylys.initProductTiles = initAll;
})();
